import { useQuery } from '@tanstack/react-query';
import { ArrowDownUp, Bell, CheckCheck } from 'lucide-react';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  fetchComunicacoes,
  fetchComunicacoesStats,
  markAllComunicacoesLidas,
  type ComunicacaoProcessualItem,
} from '../api/client';
import { useNotification } from '@/context/NotificationProvider';
import { useOfflineMutation } from '@/hooks/useOfflineMutation';
import { queryKeys } from '@/lib/pwa/queryConfig';
import { queryPersistMeta } from '@/lib/pwa/queryPersistence';
import { ComunicacaoSheet } from '../components/comunicacoes/ComunicacaoSheet';
import { ComunicacaoListItem } from '../components/comunicacoes/ComunicacaoListItem';
import { DataTablePagination } from '@/components/DataTablePagination';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { BarraDaLista } from '@/components/ui/barra-da-lista';
import { BuscaInteligente } from '@/components/ui/smart-search';
import { FiltroDaLista, type OpcaoDoFiltro } from '@/components/ui/filtro-da-lista';
import { Spinner } from '../components/ui/spinner';
import { AbasDaLista } from '../components/ui/abas-da-lista';
import { EmptyState } from '@/components/ui/EmptyState';
import { PageHeader } from '@/components/ui/PageHeader';
import { EventFormDialog } from '@/components/EventFormDialog';
import { buildPrazoEventDefaults } from '@/lib/comunicacoes/prazoEvento';
import { classifyError } from '../lib/errors';
import { useDebounce } from '../hooks/useDebounce';
import { useRigorDaBusca } from '@/hooks/useSmartSearch';
import { usePodeColaborar } from '@/lib/delegacao/atuacao';
import { OPCOES_DE_ORIGEM, useSouGestor, useVisaoDoEscritorio } from '@/hooks/useVisaoDoEscritorio';
import { podePreparar, type OrigemDaVisao } from '@/api/client';

type TabFilter = 'todas' | 'nao-lidas' | 'lidas';
type SortOption = 'recentes' | 'antigas' | 'nao-lidas';

const DEFAULT_PAGE_SIZE = 20;

/** "Com prazo" e "Novas hoje" eram dois botões; viraram um filtro, que também os combina. */
type Recorte = 'todas' | 'com-prazo' | 'novas-hoje' | 'novas-hoje-com-prazo';
const RECORTES: OpcaoDoFiltro[] = [
  { value: 'com-prazo', label: 'Com prazo' },
  { value: 'novas-hoje', label: 'Novas hoje' },
  { value: 'novas-hoje-com-prazo', label: 'Novas hoje, com prazo' },
];

function ComunicacoesPage() {
  const { showSuccess } = useNotification();
  const [searchParamsCom, setSearchParamsCom] = useSearchParams();
  const [tab, setTab] = useState<TabFilter>('todas');
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  // Filtros de triagem (fila de trabalho).
  const [comPrazo, setComPrazo] = useState(false);
  const [novasHoje, setNovasHoje] = useState(false);
  const [tribunal, setTribunal] = useState<string | undefined>(undefined);
  const [sort, setSort] = useState<SortOption>('recentes');
  // Visão do escritório (R13): as comunicações dos processos que colegas compartilham,
  // cada uma com quem a recebeu. Filtrada no servidor: a lista é paginada.
  const visaoAtiva = useVisaoDoEscritorio();
  const souGestor = useSouGestor();
  // "Minhas" (o de todo dia) ou "Todo o escritório" (só o gestor).
  const [origem, setOrigem] = useState<OrigemDaVisao>('todos');
  const origemParam = visaoAtiva ? (souGestor && origem === 'escritorio' ? 'escritorio' : 'todos') : undefined;
  const [prazoEventCom, setPrazoEventCom] = useState<ComunicacaoProcessualItem | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(searchParamsCom.get('com'));

  // A mesma busca inteligente do Acervo, feita no servidor (backend: repositories/busca_inteligente.py).
  const [rigor, setRigor] = useRigorDaBusca();
  const debouncedSearch = useDebounce(searchInput, 400);
  const searchParam = debouncedSearch.length >= 2 ? debouncedSearch : undefined;

  const lidaFilter = tab === 'nao-lidas' ? false : tab === 'lidas' ? true : undefined;

  const { data, isLoading, dataUpdatedAt, error } = useQuery({
    queryKey: [
      ...queryKeys.comunicacoes.all,
      {
        lida: lidaFilter,
        search: searchParam,
        rigor: searchParam ? rigor : undefined,
        tribunal,
        comPrazo,
        novasHoje,
        sort,
        origem: origemParam,
        offset: (page - 1) * pageSize,
      },
    ],
    meta: queryPersistMeta.nonEmpty,
    queryFn: () =>
      fetchComunicacoes({
        lida: lidaFilter,
        search: searchParam,
        rigor: searchParam && rigor === 'strict' ? rigor : undefined,
        tribunal,
        com_prazo: comPrazo || undefined,
        novas_hoje: novasHoje || undefined,
        sort: sort !== 'recentes' ? sort : undefined,
        limit: pageSize,
        offset: (page - 1) * pageSize,
        origem: origemParam,
      }),
  });

  const { data: stats, error: statsError } = useQuery({
    queryKey: [...queryKeys.comunicacoes.stats],
    queryFn: fetchComunicacoesStats,
    meta: queryPersistMeta.nonEmpty,
  });

  const podeColaborar = usePodeColaborar();
  const markAllMutation = useOfflineMutation({
    mutationFn: markAllComunicacoesLidas,
    outbox: {
      type: 'comunicacao_todas_lidas',
      getPayload: () => ({ method: 'PATCH' as const, url: '/comunicacoes/mark-all-read' }),
      getDescription: () => 'Marcar todas comunicações como lidas',
    },
    invalidateKeys: [
      [...queryKeys.comunicacoes.all],
      [...queryKeys.comunicacoes.stats],
      [...queryKeys.dashboard.stats],
    ],
    onSuccess: () => {
      showSuccess('Todas as comunicações foram marcadas como lidas');
    },
    onQueued: () => {
      showSuccess('Comunicações serão marcadas como lidas quando houver conexão');
    },
  });

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / pageSize);
  const naoLidas = stats?.nao_lidas ?? 0;
  const tribunais = stats?.tribunais ?? [];
  const hasFilters = !!searchParam || comPrazo || novasHoje || !!tribunal;

  const recorte: Recorte =
    comPrazo && novasHoje ? 'novas-hoje-com-prazo' : comPrazo ? 'com-prazo' : novasHoje ? 'novas-hoje' : 'todas';
  const mudarRecorte = (v: string) => {
    setComPrazo(v === 'com-prazo' || v === 'novas-hoje-com-prazo');
    setNovasHoje(v === 'novas-hoje' || v === 'novas-hoje-com-prazo');
    setPage(1);
  };

  const clearFilters = () => {
    setComPrazo(false);
    setNovasHoje(false);
    setTribunal(undefined);
    setPage(1);
  };

  const handleTabChange = (value: string) => {
    setTab(value as TabFilter);
    setPage(1);
  };

  const handleSheetClose = () => {
    setSelectedId(null);
    // Clear ?com= param if present
    if (searchParamsCom.has('com')) {
      setSearchParamsCom(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete('com');
          return next;
        },
        { replace: true },
      );
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        icon={<Bell />}
        title="Comunicações"
        subtitle="Intimações, citações e notificações do Diário de Justiça Eletrônico"
        dataUpdatedAt={dataUpdatedAt}
      />

      {/* Barra da lista (docs/PLANO_BARRA_DE_LISTA.md): abas à esquerda, a busca no resto. */}
      <BarraDaLista
        abas={
          <AbasDaLista
            nome="Leitura"
            value={tab}
            onValueChange={handleTabChange}
            abas={[
              { value: 'todas', label: 'Todas' },
              { value: 'nao-lidas', label: 'Não lidas', alerta: naoLidas > 0 ? { n: naoLidas, sempre: true } : undefined },
              { value: 'lidas', label: 'Lidas' },
            ]}
          />
        }
        busca={
          <BuscaInteligente
            value={searchInput}
            onChange={(val) => {
              setSearchInput(val);
              setPage(1);
            }}
            strictness={rigor}
            onStrictnessChange={(v) => {
              setRigor(v);
              setPage(1);
            }}
            placeholder="Buscar por número, classe, assunto ou texto da intimação…"
            contagem={searchParam && data ? `${total} ${total === 1 ? 'intimação' : 'intimações'}` : undefined}
          />
        }
        filtros={
          <>
            {/* A origem vem primeiro: os demais filtros refinam dentro dela (R13). */}
            {visaoAtiva && souGestor && (
              <FiltroDaLista
                eixo="Intimações"
                nome="Intimações"
                value={origem}
                onValueChange={(v) => {
                  setOrigem((v || 'todos') as OrigemDaVisao);
                  setPage(1);
                }}
                vazio="todos"
                rotuloVazio="Minhas"
                opcoes={OPCOES_DE_ORIGEM}
              />
            )}
            <FiltroDaLista
              eixo="Mostrar"
              value={recorte}
              onValueChange={mudarRecorte}
              vazio="todas"
              rotuloVazio="Todas as intimações"
              opcoes={RECORTES}
            />
            {tribunais.length > 0 && (
              <FiltroDaLista
                eixo="Tribunal"
                value={tribunal ?? 'todos'}
                onValueChange={(v) => {
                  setTribunal(v === 'todos' ? undefined : v);
                  setPage(1);
                }}
                vazio="todos"
                rotuloVazio="Todos os tribunais"
                opcoes={tribunais.map((t) => ({ value: t, label: t }))}
                buscavel={tribunais.length > 8}
              />
            )}
            <FiltroDaLista
              eixo="Mais recentes"
              nome="Ordem"
              icone={<ArrowDownUp className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />}
              value={sort}
              onValueChange={(v) => {
                setSort(v as SortOption);
                setPage(1);
              }}
              vazio="recentes"
              rotuloVazio="Mais recentes"
              opcoes={[
                { value: 'antigas', label: 'Mais antigas' },
                { value: 'nao-lidas', label: 'Não lidas primeiro' },
              ]}
            />
            {(comPrazo || novasHoje || tribunal) && (
              <Button
                variant="ghost"
                size="sm"
                className="min-h-11 text-muted-foreground sm:min-h-9"
                onClick={clearFilters}
              >
                Limpar
              </Button>
            )}
          </>
        }
        filtrosAtivos={[origem !== 'todos', recorte !== 'todas', !!tribunal].filter(Boolean).length}
        acoes={
          naoLidas > 0 && podeColaborar && (
            <Button
              variant="outline"
              className="h-11 w-11 shrink-0 whitespace-nowrap p-0 sm:h-9 sm:w-auto sm:px-4"
              onClick={() => markAllMutation.mutate()}
              disabled={markAllMutation.isPending}
              aria-label="Marcar todas lidas"
            >
              <CheckCheck className="h-4 w-4 sm:mr-1.5" />
              <span className="hidden sm:inline">Marcar todas lidas</span>
            </Button>
          )
        }
      />

      {/* Erro inline */}
      {(error || statsError) && (
        <Alert variant="destructive">
          <AlertDescription>{classifyError(error || statsError).message}</AlertDescription>
        </Alert>
      )}

      {/* Lista */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex justify-center py-12">
              <Spinner size={32} label="Carregando comunicações" />
            </div>
          ) : items.length > 0 ? (
            <div className="divide-y divide-border">
              {items.map((com: ComunicacaoProcessualItem) => (
                <ComunicacaoListItem
                  key={com.id}
                  com={com}
                  onSelect={setSelectedId}
                  onCadastrarPrazo={!podePreparar(com) || !podeColaborar ? undefined : setPrazoEventCom}
                  showTipo
                />
              ))}
            </div>
          ) : (
            <EmptyState
              icon={<Bell />}
              title={
                hasFilters ? 'Nenhuma comunicação encontrada para estes filtros' : 'Nenhuma comunicação encontrada'
              }
            />
          )}

          {/* Paginação */}
          <DataTablePagination
            page={page}
            pageSize={pageSize}
            total={total}
            pages={totalPages}
            onPageChange={setPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(1);
            }}
          />
        </CardContent>
      </Card>

      {/* Sheet de detalhes */}
      <ComunicacaoSheet comunicacaoId={selectedId} onClose={handleSheetClose} />

      {prazoEventCom && (
        <EventFormDialog
          open
          onClose={() => setPrazoEventCom(null)}
          defaultValues={buildPrazoEventDefaults(prazoEventCom)}
        />
      )}
    </div>
  );
}

export default ComunicacoesPage;
